import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '@/lib/cn';

const base =
  'min-w-0 rounded-lg border border-line bg-surface px-2.5 text-[13px] text-ink placeholder:text-ink3 disabled:opacity-60 disabled:bg-bg2 aria-[invalid=true]:border-bad';

/** Full width by default; a caller that sets its own w-* class keeps it. */
const width = (className: string | undefined) =>
  className && (className.startsWith('w-') || className.includes(' w-')) ? '' : 'w-full';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean | undefined;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid ? true : undefined}
      className={cn(base, width(className), 'h-[34px]', className)}
      {...rest}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean | undefined;
}
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid ? true : undefined}
      className={cn(base, width(className), 'min-h-16 py-2', className)}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean | undefined;
}
/** Native select with the mockup's chevron; keyboard and screen-reader friendly. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid ? true : undefined}
      className={cn(
        base,
        width(className),
        'h-[34px] cursor-pointer appearance-none bg-no-repeat pr-7 font-medium',
        "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238a857d' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")] bg-[position:right_8px_center]",
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
});
